import { WikiColumnDownloadDto } from 'collaboration-service';
import SafeHTML from 'components/SafeHTML/SafeHTML';
import WikiEditTable from 'components/Wiki/WikiEditTable';
import { Icon, Image } from 'imaginarity-react-ui';
import * as _ from "lodash";
import * as React from 'react';
import { getMediaLink } from 'services/Helpers/MediaHelpers';
import { getTranslated } from 'services/Helpers/TranslationHelpers';
import WikiHotSpot from './WikiHotSpot';
import { WikiSC as T } from './WikiSC';

export type WikiColumnContentProps = {
    column: WikiColumnDownloadDto;
    contentLanguage: string | undefined;
    htmlNotAvailable: boolean;
    tableNotAvailable: boolean;
    mediaNotAvailable: boolean;
    text: string;
    table: any;
    media: any;
    isOverlayImage: boolean;
    overlayData: any | undefined;
    aspect: number;
    w: number | undefined;
    width: number;
    imgWidth: number;
    imgHeight: number;
    uri: string | undefined;
    calcedContentWidth: number;
    isMobile: boolean;
    shortLang: string;
    contentWidth: string | number;
    columnCount: number;
    t: (key: string, opts?: any) => string;
    tGeneral: (key: string) => string;
    emptyImageUrl: string;
    missingImageSvg: string;
    missingVideoSvg: string;
    missingAudioSvg: string;
    emptyVideoThumbnail: string;
    setImageModal: (modal: JSX.Element) => void;
    removeImageModal: () => void;
    vidRef: React.RefObject<HTMLVideoElement>;
    setRef: (ref?: any) => void;
};

const WikiColumnContent = (p: WikiColumnContentProps) => {
    const {
        column, contentLanguage, htmlNotAvailable, tableNotAvailable, mediaNotAvailable,
        text, table, media, isOverlayImage, overlayData, aspect, w, width, imgWidth, imgHeight,
        uri, calcedContentWidth, isMobile, shortLang, contentWidth, columnCount,
        t, tGeneral, emptyImageUrl, missingImageSvg, missingVideoSvg, missingAudioSvg,
        emptyVideoThumbnail, setImageModal, removeImageModal, vidRef, setRef,
    } = p;

    return (
        <>
            {column.columnType === "Text" &&
                <T.ColumnContent lang={contentLanguage} info={htmlNotAvailable}>
                    {htmlNotAvailable ?
                        <div style={{ padding: 10 }}>
                            <Icon name="exclamation circle" color="@accentRed" size={24} marginTop={-2} style={{ float: "right" }} />
                            <SafeHTML html={t('there is no translated version available for this <b>html</b> in <b>{{lng}}</b>', { lng: tGeneral(shortLang) })} />
                        </div>
                        :
                        <SafeHTML html={text} />
                    }
                </T.ColumnContent>
            }
            {column.columnType === "Table" &&
                <T.ColumnContent lang={contentLanguage} info={tableNotAvailable} style={{ maxWidth: (Number(contentWidth) / (isMobile ? 1 : column.columnWidth > 1 ? 1 : columnCount) - (isMobile ? 40 : 10)) }}>
                    {tableNotAvailable ?
                        <div style={{ padding: 10 }}>
                            <Icon name="exclamation circle" color="@accentRed" size={24} marginTop={-2} style={{ float: "right" }} />
                            <SafeHTML html={t('there is no translated version available for this <b>table</b> in <b>{{lng}}</b>', { lng: tGeneral(shortLang) })} />
                        </div>
                        : <WikiEditTable
                            noEdit
                            table={table}
                            onChange={() => { }}
                        />
                    }
                </T.ColumnContent>
            }
            {column.columnType === "Image" &&
                <T.ColumnContentImage imgWidth={media?.width} ref={setRef} lang={contentLanguage} info={mediaNotAvailable}>
                    {mediaNotAvailable ?
                        <div style={{ padding: 10 }}>
                            <Icon name="exclamation circle" color="@accentRed" size={24} marginTop={-2} style={{ float: "right" }} />
                            <SafeHTML html={t('there is no translated version available for this <b>image</b> in <b>{{lng}}</b>', { lng: tGeneral(shortLang) })} />
                        </div>
                        : isOverlayImage && overlayData ?
                            <T.HotSpotContainer aspect={aspect} width={w}>
                                <T.HotSpotContainerImage>
                                    <Image src={uri ?? emptyImageUrl} mediaType={media.mediaType} width={media.mediaType === "image/svg+xml" ? "100%" : undefined} />
                                </T.HotSpotContainerImage>
                                {_.map(overlayData.elements, (e, i) => {
                                    const idx = Number(i);
                                    const delay = (idx + 1) * 0.5;
                                    return (
                                        <WikiHotSpot
                                            key={i}
                                            element={e}
                                            mediaOverlay={column.mediaOverlay}
                                            index={idx}
                                            delay={delay}
                                            imgWidth={imgWidth}
                                            imgHeight={imgHeight}
                                            width={width}
                                            offsetY={0}
                                        />
                                    );
                                })}
                            </T.HotSpotContainer>
                            :
                            <Image
                                src={uri ?? missingImageSvg}
                                onClickReturnModal={setImageModal}
                                onRemoveModal={removeImageModal}
                                mediaType={media.mediaType}
                                maxModalWidth={calcedContentWidth + "px"}
                            />
                    }
                </T.ColumnContentImage>
            }
            {column.columnType === "Video" &&
                <T.ColumnContent lang={contentLanguage} info={mediaNotAvailable}>
                    {mediaNotAvailable ?
                        <div style={{ padding: 10 }}>
                            <Icon name="exclamation circle" color="@accentRed" size={24} marginTop={-2} style={{ float: "right" }} />
                            <SafeHTML html={t('there is no translated version available for this <b>video</b> in <b>{{lng}}</b>', { lng: tGeneral(shortLang) })} />
                        </div>
                        : uri ?
                            <video
                                poster={getMediaLink(column, y => _.first(getTranslated(y.mediaContent, contentLanguage).thumbnails)) ?? emptyVideoThumbnail}
                                src={uri}
                                controls preload="auto"
                                width="100%"
                                style={{ outline: "none" }}
                                crossOrigin="anonymous"
                                ref={vidRef}
                            >
                                {_.map(media.subtitles, (sub) => {
                                    const l = _.find(sub.links, l => l.ref === "self");
                                    const label = t(`tube::${sub.lng}`)
                                    if (l && label) {
                                        return (
                                            <track key={sub.reference} id={sub.lng} src={l?.uri} label={label} kind="captions" srcLang={sub.lng} default={false} />
                                        );
                                    }
                                    return null;
                                })}
                            </video>
                            :
                            <Image src={missingVideoSvg} mediaType="svg+xml" />
                    }
                </T.ColumnContent>
            }
            {column.columnType === "Audio" &&
                <T.ColumnContent lang={contentLanguage} info={mediaNotAvailable}>
                    <T.AudioContainerHeader>
                        <Icon name="audio" style={{ float: "left", marginRight: 10 }} />{t("audiofile")}
                    </T.AudioContainerHeader>
                    {mediaNotAvailable ?
                        <div style={{ padding: 10 }}>
                            <Icon name="exclamation circle" color="@accentRed" size={24} marginTop={-2} style={{ float: "right" }} />
                            <SafeHTML html={t('there is no translated version available for this <b>audio</b> in <b>{{lng}}</b>', { lng: tGeneral(shortLang) })} />
                        </div>
                        : uri ?
                            <audio
                                src={uri}
                                controls
                                crossOrigin="anonymous"
                                controlsList="nodownload"
                                style={{ outline: "none", width: "100%", height: 40, background: "#F1F3F4" }}
                            />
                            :
                            <Image src={missingAudioSvg} mediaType="svg+xml" />
                    }
                </T.ColumnContent>
            }
        </>
    );
};

export default WikiColumnContent;
